import { useCallback, useEffect, useState } from 'react';

import client from '../api/client';

// Admin CRUD for categories (/api/category, requireAdmin for mutations).
// Lists with includeInactive=true so admins see inactive categories too.
// Category fields: { id, _id, name, slug, displayOrder, active, isDeleted }.
// Rename / reorder (displayOrder) / toggle active are all done via PUT.
// DELETE is a soft delete; the server refuses (409) if products still ref it.
// Pass includeDeleted: true to also list soft-deleted categories (so a
// restore() target can be found — they're scoped out of every normal query).
export default function useAdminCategories({ includeDeleted = false } = {}) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    const { data } = await client.get('/category', { params: { includeInactive: true, includeDeleted } });
    setCategories(Array.isArray(data) ? data : []);
  }, [includeDeleted]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    client
      .get('/category', { params: { includeInactive: true, includeDeleted } })
      .then(({ data }) => {
        if (active) setCategories(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (active) {
          setCategories([]);
          setError(err);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [includeDeleted]);

  const create = useCallback(
    async (body) => {
      const { data } = await client.post('/category', body);
      await reload();
      return data;
    },
    [reload]
  );

  const update = useCallback(
    async (id, body) => {
      const { data } = await client.put(`/category/${id}`, body);
      await reload();
      return data;
    },
    [reload]
  );

  const remove = useCallback(
    async (id) => {
      await client.delete(`/category/${id}`);
      await reload();
    },
    [reload]
  );

  const restore = useCallback(
    async (id) => {
      const { data } = await client.post(`/category/${id}/restore`);
      await reload();
      return data;
    },
    [reload]
  );

  // permanent — only allowed once a category is already soft-deleted; the
  // server severs any dangling parentId links to it before removing it.
  const hardDelete = useCallback(
    async (id) => {
      await client.delete(`/category/${id}/permanent`);
      await reload();
    },
    [reload]
  );

  return { categories, loading, error, reload, create, update, remove, restore, hardDelete };
}
